// PovifyCreateMy.js
import React, { useCallback, useEffect, useState } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GeneratePOVCode } from '../../components/GenerateThink5whysCode';

const PovifyCreateMy = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [verifyTeamSuccess, setVerifyTeamSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [TeamCreatorName, setTeamCreatorName] = useState('');
  const [rcaCreatorName, setRCACreatorName] = useState('');
  const [teamCodeStatus, setTeamCodeStatus] = useState(false);
  const [teamTitle, setTeamTitle] = useState('');
  const [teamStatusMsg, setTeamStatusMsg] = useState('');
  const [rcaCodeStatus, setRCACodeStatus] = useState(false);
  const [rcaTitle, setRCATitle] = useState('');
  const [rcaCodeStatusMsg, setRCACodeStatusMsg] = useState('');
  const userId = authState.user._id;

  const [formData, setFormData] = useState({
    povifyCode: GeneratePOVCode(),
    creatorId: authState.user._id,
    creatorName: authState.user.name,
    povifyVisibility: 'Personal',
    povifyTeamCode: '',
    povifyTitle: '',
    povifyBrief: '',
    povifyDetails: '',
    povifyImage: '',
    povifyTeamName: '',
    povifyRCACode: '',
    dateStarted: new Date(),
    isActive: 1,
    isDeleted: 0,
  });

  const [createClicked, setCreateClicked] = useState(false);

  const handleCreateClick = () => {
    if (formData.povifyTitle === '') {
      setErrors({ povifyTitle: "Title Must Not Be Blank" });
      return;
    }
    setCreatingTeam(true);
    fetchPOVCreate();
  };

  const fetchPOVCreate = useCallback(() => {
    const config = {
      url: "/povify",
      method: 'post',
      data: formData,
      headers: { Authorization: authState.token },
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setCreateSuccess(true);
          setCreateSuccessMsg("POV Created Successfully");
          setCreateClicked(true);
        } else {
          setCreateSuccessMsg("Sorry! POV Not Created. Please retry.");
        }
        setCreatingTeam(false);
      })
      .catch(() => {
        setCreateSuccessMsg("Sorry! POV Not Created. Please retry.");
        setCreatingTeam(false);
      });
  }, [formData, authState, fetchData]);

  const handleVerifyTeamClick = () => {
    setErrors({});
    setVerifyTeamSuccess(true);
    if (/^TT[a-zA-Z0-9]{12}$/.test(formData.povifyTeamCode)) fetchTeamCode();
    else {
      setErrors({ povifyTeamCode: 'Please enter a valid 14-character Think5Whys Team Code starting with TT.' });
      setVerifyTeamSuccess(false);
    }
  };

  const fetchTeamCode = useCallback(() => {
    const config = {
      url: "/myteams/verifyteamcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { teamCode: formData.povifyTeamCode }
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setTeamCodeStatus(true);
          setTeamTitle(response.teamcodeinfo.teamTitle);
          setTeamCreatorName(response.teamcodeinfo.creatorName);
          setTeamStatusMsg("Parent Team Code found. 😊");
        } else setTeamStatusMsg(`Team Code ${formData.povifyTeamCode} not found! 😔`);
      })
      .catch(() => setTeamStatusMsg("Error in getting data"));
  }, [fetchData, authState.token, formData.povifyTeamCode]);

  const [verifyRCACodeSuccess, setVerifyRCACodeSuccess] = useState(false);
  const handleVerifyRCACodeClick = () => {
    setErrors({});
    setVerifyRCACodeSuccess(true);
    if (/^TP[a-zA-Z0-9]{12}$/.test(formData.povifyRCACode)) fetchRCACode();
    else {
      setErrors({ povifyRCACode: 'Please enter a valid 14-character Think5Whys RCA Code starting with TP.' });
      setVerifyRCACodeSuccess(false);
    }
  };

  const fetchRCACode = useCallback(() => {
    const config = {
      url: "/userrca/verifyrcacode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { rcaCode: formData.povifyRCACode }
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setRCACodeStatus(true);
          setRCATitle(response.rcainfo.rcaTitle);
          setRCACreatorName(response.rcainfo.creatorName);
          setRCACodeStatusMsg("RCA Code found. 😊");
        } else setRCACodeStatusMsg(`RCA Code ${formData.povifyRCACode} not found! 😔`);
      })
      .catch(() => setRCACodeStatusMsg("Error in getting data"));
  }, [fetchData, authState.token, formData.povifyRCACode]);

  const handleBackToMyPOVListClick = () => navigate('/povify-my');

  const handleUpdateClick = async () => {
    try {
      const response = await fetch(`/povify/${formData._id}`, {
        method: 'put',
        headers: { 'Content-Type': 'application/json', 'Authorization': authState.token },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setUpdateSuccess(true);
        setUpdateSuccessMsg('POV updated successfully.');
      }
    } catch (error) {
      console.error('Error updating POV:', error.message);
    }
  };

  return (
    <div className="bg-gray-950 text-gray-100 min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-gray-900 border border-teal-500 p-4 text-center shadow-md">
        <h1 className="text-2xl md:text-3xl font-semibold text-yellow-300">POVify</h1>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 py-6 w-full mx-auto">
        <h2 className="text-center text-lg lg:text-xl text-yellow-200 mb-2 font-medium">
          Create Point Of View
        </h2>

        <div className="mb-2">
          <p className="text-yellow-400 text-sm md:text-base">
            POV Code: <strong>{formData.povifyCode}</strong>
          </p>
        </div>

        {/* Title */}
        <div className="flex flex-col gap-3 mb-2 border-b border-gray-700 pb-4">
          <label className="text-sm md:text-base">POV Headline</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              className="rounded-md px-3 py-2 text-black w-full"
              value={formData.povifyTitle}
              style={{width: "100%"}}
              onChange={(e) => setFormData({ ...formData, povifyTitle: e.target.value })}
              placeholder="Enter POV title"
            />
            <button
              className="bg-orange-700 hover:bg-orange-600 rounded-md w-40 px-4 py-2 text-white text-sm font-medium disabled:opacity-50"
              onClick={handleCreateClick}
              disabled={creatingTeam || createSuccess}
            >
              {creatingTeam ? 'Creating...' : 'Create POV'}
            </button>
          </div>
        </div>

        {createClicked && createSuccess && (
          <div className="mb-2 p-4 rounded-md bg-green-900/40 border border-green-500 text-green-400 text-center">
            POV "{formData.povifyTitle}" Created Successfully! <br />
            <span className="text-orange-400">Fill the other details!</span>
          </div>
        )}

        {/* Errors */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-2 p-4 rounded-md bg-red-900/40 border border-red-500 text-red-400 text-sm">
            {Object.values(errors).map((error, i) => (
              <p key={i}>{error}</p>
            ))}
          </div>
        )}

        {/* Visibility */}
        <div className="flex flex-col mb-2">
          <label className="mb-2 text-sm md:text-base">Visibility</label>
          <select
            className="rounded-md w-20 px-3 py-2 text-black"
            value={formData.povifyVisibility}
            onChange={(e) => setFormData({ ...formData, povifyVisibility: e.target.value })}
          >
            <option value="Self">Self</option>
            <option value="Team">Team</option>
            <option value="Public">Public</option>
          </select>
        </div>

        {/* Text Areas */}
        {[
          { label: 'Brief Info', key: 'povifyBrief', rows: 3, placeholder: 'Enter POV description' },
          { label: 'POV in Details', key: 'povifyDetails', rows: 6, placeholder: 'Enter POV details' },
          { label: 'POV Image (URL)', key: 'povifyImage', rows: 2, placeholder: 'Enter image URL' },
        ].map(({ label, key, rows, placeholder }) => (
          <div className="flex flex-col mb-2" key={key}>
            <label className="mb-2 text-sm md:text-base">{label}</label>
            <textarea
              rows={rows}
              className="rounded-md px-3 py-2 text-black"
              style={{widt: "100%", height:"300px"}}
              value={formData[key]}
              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
              placeholder={placeholder}
            />
          </div>
        ))}

        <div className='border border-teal-500 px-4 py-2'>
          {/* RCA Code */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-2">
            <div className="flex flex-col flex-1">
              <label className="mb-2 text-sm lg:text-lg">RCA Code (If Any)</label>
              <input
                type="text"
                className="rounded-md px-3 py-2 text-black w-full"
                value={formData.povifyRCACode}
                onChange={(e) => setFormData({ ...formData, povifyRCACode: e.target.value })}
                placeholder="Enter RCA code"
              />
              {rcaCodeStatusMsg && (
                <p className={`mt-1 text-xs ${rcaCodeStatus ? 'text-green-400' : 'text-red-400'}`}>
                  {rcaCodeStatusMsg}
                </p>
              )}
            </div>
            <button
              className="bg-orange-700 hover:bg-orange-600 w-40 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              onClick={handleVerifyRCACodeClick}
              disabled={verifyRCACodeSuccess}
            >
              Verify RCA
            </button>
          </div>

          {/* Team Code */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-2">
            <div className="flex flex-col flex-1">
              <label className="mb-2 text-sm lg:text-lg">Team Code</label>
              <input
                type="text"
                className="rounded-md px-3 py-2 text-black w-full"
                value={formData.povifyTeamCode}
                onChange={(e) => setFormData({ ...formData, povifyTeamCode: e.target.value })}
                placeholder="Enter team code"
              />
              {teamStatusMsg && (
                <p className={`mt-1 text-xs ${teamCodeStatus ? 'text-green-400' : 'text-red-400'}`}>
                  {teamStatusMsg}
                </p>
              )}
              {teamTitle && (
                <p className="mt-1 text-xs text-gray-300">
                  Team: {teamTitle} (Creator: {TeamCreatorName})
                </p>
              )}
            </div>
            <button
              className="bg-orange-700 hover:bg-orange-600 w-40 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              onClick={handleVerifyTeamClick}
              disabled={verifyTeamSuccess}
            >
              Verify Team
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-row gap-4 justify-start mt-4">
          <button
            className="bg-purple-700 hover:bg-purple-600 px-6 py-2 rounded-md w-full md:w-auto"
            onClick={handleUpdateClick}
          >
            Update
          </button>
          <button
            className="bg-purple-700 hover:bg-purple-600 px-6 py-2 rounded-md w-full md:w-auto"
            onClick={handleBackToMyPOVListClick}
          >
            Back to POV List
          </button>
        </div>

        {updateSuccess && (
          <div className="mt-6 p-4 rounded-md bg-green-900/40 border border-green-500 text-center">
            <p className="text-green-400">{updateSuccessMsg}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PovifyCreateMy;
